import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import sessionManager from './utils/sessionManager';
import Logo from './components/Logo';
import GoogleAddressAutocomplete from './components/GoogleAddressAutocomplete';
import './CreditPullPage.css';
import API_CONFIG from './config';

const CreditPullPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [showPasswordSetup, setShowPasswordSetup] = useState(false);
  
  const [creditFormData, setCreditFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    dateOfBirth: '',
    ssn: '',
    driverLicense: '',
    email: '',
    phoneNumber: '',
    fullAddress: '',
    consentChecked: false,
    userId: null // User ID for credit report processing
  });

  // Load user profile data on component mount
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        // Check if user is authenticated
        const sessionData = sessionManager.getUserInfo();
        if (!sessionData?.authToken) {
          navigate('/');
          return;
        }

        // Fetch user profile data
        const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.GET_TENANT_PROFILE}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${sessionData.authToken}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.profile) {
            // Prepopulate form with existing profile data
            setCreditFormData(prev => ({
              ...prev,
              firstName: data.profile.first_name || '',
              lastName: data.profile.last_name || '',
              email: data.profile.email || '',
              phoneNumber: data.profile.username || sessionData.phoneNumber || '', // username is phone number for web users
              userId: data.profile.id // Store user ID for credit report processing
            }));
          }
        }
      } catch (error) {
        console.error('Error loading user profile:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserProfile();
  }, [navigate]);

  const handleCreditFormChange = (field, value) => {
    setCreditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCreditFormSubmit = async (e) => {
    e.preventDefault();
    
    // Check if user is authenticated
    const sessionData = sessionManager.getUserInfo();
    if (!sessionData?.authToken) {
      alert('User session not found. Please log in again.');
      navigate('/');
      return;
    }
    
    try {
      // Get auth token from session
      const sessionData = sessionManager.getUserInfo();
      console.log('Session data:', sessionData);
      console.log('Session manager methods:', {
        isAuthenticated: sessionManager.isAuthenticated(),
        getSession: sessionManager.getSession()
      });
      
      if (!sessionData?.authToken) {
        console.error('No auth token found in session data:', sessionData);
        alert('User session not found. Please log in again.');
        navigate('/');
        return;
      }
      
      console.log('Using auth token:', sessionData.authToken);
      
      // Build Equifax OneView request body
      const requestBody = {
        // Include user ID for explicit identification
        userId: creditFormData.userId,
        // Equifax applicant data
        applicant: {
          firstName: creditFormData.firstName,
          middleName: creditFormData.middleName || undefined,
          lastName: creditFormData.lastName,
          email: creditFormData.email,
          phoneNumber: creditFormData.phoneNumber,
          dateOfBirth: creditFormData.dateOfBirth,
          ssn: creditFormData.ssn || undefined,
          driverLicense: creditFormData.driverLicense || undefined,
          address: creditFormData.fullAddress,
        }
      };

      // Send request to secure endpoint with auth token in headers
      const resp = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.EQUIFAX_ONEVIEW}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.authToken}`
        },
        body: JSON.stringify(requestBody)
      });
      const data = await resp.json();
      console.log('Secure credit report response:', data);
      
      if (!resp.ok || !data.success) {
        console.error('Credit report error:', data);
        
        // Check if error is related to missing password (exact match to avoid false positives)
        if (data.error && data.error.includes('Password required for credit report encryption')) {
          console.log('User needs to set up password for credit report encryption');
          setShowPasswordSetup(true);
          return;
        }
        
        alert(`Failed to pull credit report: ${data.error || 'Unknown error'}`);
        return;
      }
      
      // Success - credit report stored securely in encrypted database
      alert(`✅ Credit report pulled and stored securely!

📊 Credit Score: ${data.credit_score || 'Processing...'}
📅 Report Date: ${data.report_date || 'Today'}
🔒 Encrypted Storage: Your data is protected with enterprise-grade encryption
📋 Report ID: ${data.report_id || 'Generated'}

You can now view your detailed credit report on your profile page.`);
      
      navigate('/profile');
    } catch (err) {
      console.error('Credit pull error:', err);
      alert('❌ Unexpected error occurred. Please try again later.');
    }
  };

  const handleBack = () => {
    navigate('/profile');
  };

  return (
    <div className="credit-pull-page">
      {/* Header */}
      <header className="credit-pull-header">
        <div className="header-container">
          <Logo size="medium" showText={true} variant="dark" onClick={() => navigate('/')} />
          <div className="header-actions">
            <button 
              onClick={() => navigate('/profile')} 
              className="back-btn"
              title="Back to Profile"
            >
              👤 Profile
            </button>
            <button 
              onClick={() => navigate('/chat')} 
              className="chat-btn"
              title="Back to Chat"
            >
              💬 Chat
            </button>
            <button 
              onClick={() => navigate('/')} 
              className="home-btn"
              title="Back to Home"
            >
              🏠 Home
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="credit-pull-container">
        <div className="credit-pull-content">
          <h1 className="credit-pull-title">
            Credit Report
          </h1>
          <p className="credit-pull-subtitle">
            Soft pull your credit report from Equifax
          </p>

          {loading ? (
            <div className="loading-container" style={{ textAlign: 'center', padding: '2rem' }}>
              <div className="loading-spinner" style={{ 
                border: '4px solid #f3f3f3', 
                borderTop: '4px solid #007bff', 
                borderRadius: '50%', 
                width: '40px', 
                height: '40px', 
                animation: 'spin 1s linear infinite',
                margin: '0 auto 1rem'
              }}></div>
              <p>Loading your profile information...</p>
            </div>
          ) : (

          <form onSubmit={handleCreditFormSubmit} className="credit-form">
            {/* Personal Information Grid */}
            <div className="form-section">
              <h3>Personal Information</h3>
              <div className="credit-form-grid">
                <div className="credit-form-group">
                  <label htmlFor="firstName">First name*</label>
                  <input
                    type="text"
                    id="firstName"
                    value={creditFormData.firstName}
                    onChange={(e) => handleCreditFormChange('firstName', e.target.value)}
                    placeholder="John"
                    required
                  />
                  <small>As it appears on government ID</small>
                </div>

                <div className="credit-form-group">
                  <label htmlFor="middleName">Middle name</label>
                  <input
                    type="text"
                    id="middleName"
                    value={creditFormData.middleName}
                    onChange={(e) => handleCreditFormChange('middleName', e.target.value)}
                    placeholder="Michael"
                  />
                  <small>As it appears on government ID</small>
                </div>

                <div className="credit-form-group">
                  <label htmlFor="lastName">Last name*</label>
                  <input
                    type="text"
                    id="lastName"
                    value={creditFormData.lastName}
                    onChange={(e) => handleCreditFormChange('lastName', e.target.value)}
                    placeholder="Doe"
                    required
                  />
                  <small>As it appears on government ID</small>
                </div>

                <div className="credit-form-group">
                  <label htmlFor="dateOfBirth">Date of birth*</label>
                  <input
                    type="date"
                    id="dateOfBirth"
                    value={creditFormData.dateOfBirth}
                    onChange={(e) => handleCreditFormChange('dateOfBirth', e.target.value)}
                    required
                  />
                </div>

                <div className="credit-form-group">
                  <label htmlFor="ssn">SSN/SIN</label>
                  <input
                    type="text"
                    id="ssn"
                    value={creditFormData.ssn}
                    onChange={(e) => handleCreditFormChange('ssn', e.target.value)}
                    placeholder="123212312"
                    maxLength="9"
                  />
                </div>

                <div className="credit-form-group">
                  <label htmlFor="driverLicense">Driver license number</label>
                  <input
                    type="text"
                    id="driverLicense"
                    value={creditFormData.driverLicense}
                    onChange={(e) => handleCreditFormChange('driverLicense', e.target.value)}
                    placeholder="Driver license number"
                  />
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="form-section">
              <h3>Contact Information</h3>
              <div className="credit-form-grid">
                <div className="credit-form-group">
                  <label htmlFor="email">Email*</label>
                  <input
                    type="email"
                    id="email"
                    value={creditFormData.email}
                    onChange={(e) => handleCreditFormChange('email', e.target.value)}
                    placeholder="test@email.com"
                    required
                  />
                </div>

                <div className="credit-form-group">
                  <label htmlFor="phoneNumber">Phone number*</label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    value={creditFormData.phoneNumber}
                    onChange={(e) => handleCreditFormChange('phoneNumber', e.target.value)}
                    placeholder="(555) 555-5556"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Security Information - Using account password for encryption */}
            <div className="form-section">
              <div className="security-info">
                <h3>🔒 Security Information</h3>
                <div className="security-note">
                  <small>
                    🔐 Your credit report will be encrypted using your account password.
                    <br />
                    💡 Make sure you have a password set in your account settings.
                    <br />
                    🛡️ Your data is protected with enterprise-grade encryption.
                  </small>
                </div>
              </div>
            </div>

            {/* Address Information */}
            <div className="form-section">
              <h3>Current Address</h3>
              <div className="credit-address-section">
                <div className="credit-address-header">
                  <button type="button" className="add-address-btn">
                    Add address manually
                  </button>
                </div>
                
                <div className="credit-form-group credit-address-group">
                  <label htmlFor="fullAddress">Full Address*</label>
                  <GoogleAddressAutocomplete
                    id="fullAddress"
                    value={creditFormData.fullAddress}
                    onChange={(value) => handleCreditFormChange('fullAddress', value)}
                    placeholder="220 Yonge St, Toronto, ON, Canada, M5B 2H1"
                    required={true}
                  />
                  <small>For best results use address shown on your ID</small>
                </div>
              </div>
            </div>

            {/* Consent Section */}
            <div className="form-section">
              <div className="credit-consent-section">
                <label className="credit-consent-checkbox">
                  <input
                    type="checkbox"
                    checked={creditFormData.consentChecked}
                    onChange={(e) => handleCreditFormChange('consentChecked', e.target.checked)}
                    required
                  />
                  <span className="checkmark"></span>
                  I consent to a credit check and accept RentalHelp.ai{' '}
                  <button type="button" className="terms-link" onClick={() => alert('Terms and Conditions coming soon!')}>
                    Terms and Conditions
                  </button>
                </label>
              </div>
            </div>

            {/* Form Actions */}
            <div className="credit-form-actions">
              <button 
                type="button" 
                className="credit-back-btn"
                onClick={handleBack}
              >
                ← Back
              </button>
              <button 
                type="submit" 
                className="credit-next-btn"
                disabled={!creditFormData.consentChecked}
              >
                Pull Credit Report
              </button>
            </div>
          </form>
          )}
        </div>
      </div>

      {/* Password Setup Overlay */}
      {showPasswordSetup && (
        <div className="password-setup-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div className="password-setup-modal" style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            position: 'relative',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
          }}>
            <button 
              className="close-btn" 
              onClick={() => setShowPasswordSetup(false)}
              style={{ 
                position: 'absolute', 
                top: '15px', 
                right: '20px', 
                background: 'none', 
                border: 'none', 
                fontSize: '28px', 
                cursor: 'pointer',
                color: '#666'
              }}
            >
              ×
            </button>
            
            <div className="password-setup-header" style={{ marginBottom: '1.5rem' }}>
              <h2 style={{ color: '#1f2937', marginBottom: '0.5rem' }}>Set Up Password for Credit Report Encryption</h2>
              <p style={{ color: '#666', fontSize: '14px' }}>
                To securely encrypt your credit report data, please set up a password. 
                This password will protect your sensitive financial information with enterprise-grade encryption.
              </p>
            </div>
            
            <div className="password-setup-form">
              <p style={{ 
                backgroundColor: '#fef3c7', 
                color: '#92400e', 
                padding: '12px', 
                borderRadius: '8px', 
                fontSize: '14px',
                marginBottom: '20px',
                border: '1px solid #fbbf24'
              }}>
                🔒 <strong>Security Notice:</strong> This password will be used to encrypt your credit report. 
                Make sure it's strong and memorable - you'll need it to access your encrypted reports.
              </p>
              
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <div style={{ 
                  border: '2px dashed #e5e7eb', 
                  borderRadius: '8px', 
                  padding: '2rem',
                  backgroundColor: '#f9fafb'
                }}>
                  <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
                    Click the button below to set up your password using our secure signup flow:
                  </p>
                  <button
                    onClick={() => {
                      setShowPasswordSetup(false);
                      // Navigate to a dedicated password setup or use the existing signup flow
                      navigate('/', { state: { showPasswordSetup: true } });
                    }}
                    style={{
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      fontSize: '16px',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    Set Up Password
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditPullPage;
